import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, RotateCcw, Save } from 'lucide-react';

// A text node that admins can edit by right-clicking. Non-admins see plain text.
// The chosen override is stored in site settings (settings.content[k]).
export default function EditableText({ k, text, as = 'span', className, style, multiline = true }) {
  const { settings, setContentOverride, clearContentOverride, isAdmin } = useAuth();
  const override = settings && settings.content ? settings.content[k] : undefined;
  const effective = override !== undefined && override !== null ? override : text;

  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const Tag = as;

  const onContext = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    setVal(effective || '');
    setErr('');
    setOpen(true);
  };

  const save = async () => {
    setBusy(true); setErr('');
    try { await setContentOverride(k, val); setOpen(false); }
    catch (e) { setErr('Could not save. Are you signed in as admin?'); }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true); setErr('');
    try { await clearContentOverride(k); setOpen(false); }
    catch (e) { setErr('Could not reset.'); }
    setBusy(false);
  };

  const adminStyle = isAdmin
    ? { cursor: 'context-menu', outline: '1px dashed rgba(138,10,8,0.35)', outlineOffset: 2, borderRadius: 2 }
    : null;

  return (
    <>
      <Tag
        className={className}
        style={adminStyle ? { ...(style || {}), ...adminStyle } : style}
        onContextMenu={onContext}
        data-testid={isAdmin ? `editable-text-${k}` : undefined}
        title={isAdmin ? 'Right-click to edit' : undefined}
      >
        {effective}
      </Tag>

      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => !busy && setOpen(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Edit Text</h3>
              <button onClick={() => setOpen(false)} disabled={busy} data-testid="editable-text-close"><X size={20} /></button>
            </div>

            {multiline ? (
              <textarea
                data-testid="editable-text-input"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 rounded border text-sm"
                style={{ borderColor: 'rgba(0,0,0,0.2)' }}
              />
            ) : (
              <input
                data-testid="editable-text-input"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                className="w-full px-3 py-2 rounded border text-sm"
                style={{ borderColor: 'rgba(0,0,0,0.2)' }}
              />
            )}

            {err && <p className="text-sm mt-3" style={{ color: '#8a1f11' }}>{err}</p>}

            <div className="flex items-center justify-between mt-5">
              {override !== undefined && override !== null ? (
                <button onClick={reset} disabled={busy} className="flex items-center gap-1 text-sm" style={{ color: 'var(--olive)' }} data-testid="editable-text-reset">
                  <RotateCcw size={14} /> Reset to default
                </button>
              ) : <span />}
              <button onClick={save} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="editable-text-save">
                <Save size={15} /> Save
              </button>
            </div>
            {busy && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Working…</p>}
          </div>
        </div>
      )}
    </>
  );
}
